import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Page, PageHeader } from '@/components/layout/page';
import { Alert, Container, Section } from '@/components/ui/primitives';
import { useSite } from '@/hooks/use-site';
import { describeOffer } from '@/lib/format';
import { paths } from '@/lib/paths';

/**
 * Pages légales : mentions, conditions générales, confidentialité.
 *
 * **Elles ne sont pas décoratives.** Le robot de vérification de mise en
 * production visite `/mentions-legales`, `/cgv`, `/cgu` et
 * `/conditions-generales` avant d'autoriser l'émission de la clé live : un
 * site qui n'en dispose pas voit son examen échouer.
 *
 * Les textes ci-dessous sont des **exemples de structure**, pas un modèle
 * juridique. Chaque site doit les remplacer par ses propres mentions,
 * rédigées ou validées par un juriste : la plateforme n'en est pas l'auteur
 * et n'en répond pas.
 */

function LegalDisclaimer() {
    return (
        <Alert tone="caution" title="Texte d'exemple, à remplacer">
            Ce contenu est fourni à titre d'illustration dans un site de démonstration. Il ne constitue ni un modèle
            juridique, ni un conseil : remplacez-le par les mentions propres à votre société avant toute mise en
            production.
        </Alert>
    );
}

function LegalPage({ title, eyebrow, children }: { title: string; eyebrow: string; children: ReactNode }) {
    return (
        <Page title={title}>
            <PageHeader eyebrow={eyebrow} title={title} />

            <Section>
                <Container className="max-w-3xl space-y-8">
                    <LegalDisclaimer />
                    <div className="prose-doc">{children}</div>
                </Container>
            </Section>
        </Page>
    );
}

export function LegalNoticePage() {
    const site = useSite();

    return (
        <LegalPage eyebrow="Informations légales" title="Mentions légales">
            <h2>Éditeur du site</h2>
            <p>
                Le site {site.name} est édité à titre de démonstration de l'API Dash Lite. Les coordonnées complètes de
                l'éditeur, sa forme juridique, son capital social, son numéro d'immatriculation et le nom du directeur
                de la publication doivent figurer ici.
            </p>

            <h2>Contact</h2>
            <p>
                {site.contact_email ? (
                    <>
                        Toute demande peut être adressée à{' '}
                        <a href={`mailto:${site.contact_email}`}>{site.contact_email}</a>, ou via le{' '}
                        <Link to={paths.contact}>formulaire de contact</Link>.
                    </>
                ) : (
                    <>
                        Toute demande peut être adressée via le <Link to={paths.contact}>formulaire de contact</Link>.
                    </>
                )}
            </p>

            <h2>Hébergement</h2>
            <p>
                Le nom, la raison sociale et l'adresse de l'hébergeur du site doivent être mentionnés ici, ainsi que
                ceux de l'hébergeur de l'API si celui-ci diffère.
            </p>

            <h2>Origine des données affichées</h2>
            <p>
                Les informations d'entreprises présentées proviennent des registres nationaux officiels, relayés par la
                plateforme. Elles sont restituées telles que publiées par ces registres : leur exactitude et leur
                actualité relèvent de la source, et chaque fiche indique la date à laquelle elle a été constituée.
            </p>
            <p>
                En environnement de test, les entreprises affichées sont <strong>entièrement fictives</strong> et ne
                correspondent à aucune société réelle.
            </p>

            <h2>Propriété intellectuelle</h2>
            <p>
                La structure du site, sa charte graphique et ses textes sont protégés. Les données publiques des
                registres, elles, restent la propriété de leurs producteurs respectifs.
            </p>
        </LegalPage>
    );
}

export function TermsPage() {
    const site = useSite();

    return (
        <LegalPage eyebrow="Contrat" title="Conditions générales">
            <h2>Objet</h2>
            <p>
                Les présentes conditions régissent l'accès au service de consultation d'informations d'entreprises
                proposé par {site.name}, et l'usage qui peut en être fait.
            </p>

            <h2>L'offre et son tarif</h2>
            <p>
                {site.offer ? (
                    <>
                        L'offre proposée est « {site.offer.name} » : {describeOffer(site.offer)}. Le tarif affiché est
                        celui pratiqué au moment de la commande ; il est rappelé avant tout paiement.
                    </>
                ) : (
                    "Aucune offre n'est actuellement configurée sur ce site."
                )}
            </p>

            <h2>Création du compte</h2>
            <p>
                Aucune inscription préalable n'est demandée. Le compte est créé automatiquement à la réussite du
                paiement, et un courrier électronique invite son titulaire à choisir un mot de passe. Les
                identifiants sont personnels et ne doivent pas être partagés.
            </p>

            <h2>Paiement</h2>
            <p>
                Les paiements sont traités par Stripe. Les données de carte bancaire sont transmises directement par le
                navigateur du client au prestataire de paiement : elles ne transitent ni ne sont conservées par le
                site.
            </p>

            <h2>Droit de rétractation et résiliation</h2>
            <p>
                Pour une offre par abonnement, la résiliation peut être demandée à tout moment, sans justification et
                en ligne, depuis <Link to={paths.account}>l'espace personnel</Link> ou depuis la{' '}
                <Link to={paths.cancelSubscription}>page de résiliation</Link>. L'arrêt prend effet à la fin de la
                période déjà réglée : l'accès reste ouvert jusqu'à cette échéance, et aucun nouveau prélèvement n'a
                lieu.
            </p>
            <p>
                Pour un contenu numérique fourni immédiatement, le droit de rétractation s'exerce dans les conditions
                prévues par la réglementation applicable, que ces conditions doivent détailler.
            </p>

            <h2>Disponibilité et sources</h2>
            <p>
                Le service dépend de la disponibilité des registres officiels. En cas d'indisponibilité d'une source,
                une fiche antérieure peut être servie ; elle est alors signalée comme datée. Aucune information n'est
                inventée pour combler une donnée absente.
            </p>

            <h2>Responsabilité</h2>
            <p>
                Les informations restituées sont celles publiées par les registres officiels. Elles ne constituent ni
                un conseil, ni une garantie sur la situation d'une entreprise, et ne dispensent pas de vérifications
                complémentaires lorsque l'enjeu le justifie.
            </p>
        </LegalPage>
    );
}

export function PrivacyPage() {
    const site = useSite();

    return (
        <LegalPage eyebrow="Données personnelles" title="Politique de confidentialité">
            <h2>Les données que nous traitons</h2>
            <ul>
                <li>
                    <strong>Compte</strong> : votre adresse email, la date de création du compte et l'état de votre
                    accès.
                </li>
                <li>
                    <strong>Paiement</strong> : la marque et les quatre derniers chiffres de votre carte. Le numéro
                    complet n'est jamais reçu ni conservé par le site — il est transmis directement à notre prestataire
                    de paiement.
                </li>
                <li>
                    <strong>Assistance</strong> : le contenu des messages que vous nous adressez.
                </li>
                <li>
                    <strong>Newsletter</strong> : votre adresse email, et le cas échéant votre prénom et votre nom, si
                    vous vous y inscrivez.
                </li>
            </ul>

            <h2>Pourquoi</h2>
            <p>
                Ces données servent exclusivement à fournir le service : ouvrir votre accès, traiter le paiement,
                répondre à vos demandes et, si vous l'avez accepté, vous adresser nos actualités. Elles ne sont ni
                vendues, ni cédées à des tiers à des fins publicitaires.
            </p>

            <h2>Vos droits</h2>
            <p>
                Vous pouvez à tout moment consulter et modifier les informations de votre compte, et{' '}
                <strong>supprimer définitivement ce compte</strong> depuis{' '}
                <Link to={paths.account}>votre espace personnel</Link>. La suppression révoque immédiatement votre
                accès et efface vos données, sous réserve des obligations légales de conservation, notamment
                comptables.
            </p>
            <p>
                Vous pouvez également vous désinscrire de la newsletter en un clic depuis n'importe lequel de nos
                messages, ou depuis <Link to={paths.unsubscribe}>la page de désinscription</Link>.
            </p>

            <h2>Sous-traitants</h2>
            <p>
                Le traitement des paiements est assuré par Stripe. L'hébergement du service et l'envoi des courriers
                électroniques sont assurés par la plateforme qui opère ce site. La liste complète des sous-traitants,
                leurs localisations et les garanties applicables doivent être détaillées ici.
            </p>

            <h2>Nous écrire</h2>
            <p>
                Pour toute question relative à vos données{' '}
                {site.contact_email ? (
                    <>
                        , écrivez-nous à <a href={`mailto:${site.contact_email}`}>{site.contact_email}</a>.
                    </>
                ) : (
                    <>
                        , utilisez le <Link to={paths.contact}>formulaire de contact</Link>.
                    </>
                )}
            </p>
        </LegalPage>
    );
}
